import React, { useState, useRef, useEffect } from "react";
import { Send, Volume2 } from "lucide-react";
import api from "@/lib/api";
import Mashy from "@/components/Mashy";

export default function Tutor() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste! I'm Mashy 🐶 Ask me anything in English, Hindi, or Hinglish — math ya English, dono mein help karunga!" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("english");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/tutor/respond", {
        message: userMsg.content,
        subject,
        language: "hinglish",
        history: messages.slice(-6),
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Hmm, network slow hai. Phir try karo?" }]);
    } finally {
      setSending(false);
    }
  };

  const speak = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "en-IN";
      speechSynthesis.speak(u);
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex flex-col pb-24" data-testid="tutor-screen">
      <header className="px-5 pt-6 max-w-xl mx-auto w-full">
        <div className="flex items-center gap-3">
          <Mashy mood="happy" size={56} bounce={false} />
          <div>
            <h1 className="font-display text-xl">Ask Mashy</h1>
            <div className="text-xs text-gray-500 font-bold">Your friendly AI tutor</div>
          </div>
        </div>
        <div className="flex gap-2 mt-3" data-testid="tutor-subject-tabs">
          {["english", "math"].map((s) => (
            <button key={s} onClick={() => setSubject(s)} data-testid={`tutor-tab-${s}`}
              className={`flex-1 btn-chunky text-sm ${subject === s ? "bg-[#1CB0F6] border-[#1899D6] text-white" : "bg-white border-gray-300 text-gray-600"}`}>
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <main ref={scrollRef} className="flex-1 max-w-xl mx-auto w-full px-5 mt-4 overflow-y-auto flex flex-col gap-3" data-testid="tutor-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "items-end gap-2"}`}>
            {m.role === "assistant" && <Mashy mood="happy" size={44} bounce={false} />}
            <div className={`px-4 py-3 rounded-3xl max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-[#1CB0F6] text-white rounded-br-md" : "bg-white border-2 border-gray-200 text-gray-800 rounded-bl-md"}`}>
              {m.content}
              {m.role === "assistant" && (
                <button onClick={() => speak(m.content)} data-testid={`tutor-speak-${i}`} className="block mt-1 text-xs text-[#1CB0F6] font-bold flex items-center gap-1">
                  <Volume2 className="w-3 h-3" /> Listen
                </button>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="text-xs text-gray-500 italic">Mashy is thinking…</div>}
      </main>

      <div className="max-w-xl mx-auto w-full px-5 pb-4 mt-3 sticky bottom-16 bg-[#F7F9FC]">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type your question..."
            data-testid="tutor-input"
            className="flex-1 border-2 border-gray-300 rounded-2xl px-4 py-3 focus:border-[#1CB0F6] outline-none"
          />
          <button onClick={send} disabled={sending} data-testid="tutor-send" className="btn-primary">
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
